import api from '../config/api';
import imageService from './imageService';

const advertisementService = {
  async getAll() {
    const response = await api.get('/api/advertisements/all');
    return response.data;
  },

  async getById(id) {
    const response = await api.get(`/api/advertisements/${id}`);
    return response.data;
  },

  async getDetails(id) {
    const response = await api.get(`/api/advertisements/details/${id}`);
    return response.data;
  },

  async create(advertisementData) {
    let imageUrl = advertisementData.imageUrl;
    if (advertisementData.image instanceof File) {
      const uploadResult = await imageService.uploadImages([advertisementData.image], 'advertisements');
      if (!uploadResult?.success || !uploadResult?.relativePaths?.length) {
        throw new Error(uploadResult?.message || 'Image upload failed');
      }
      imageUrl = uploadResult.relativePaths[0];
    }
    if (!imageUrl) {
      throw new Error('Image is required');
    }
    const placements = (advertisementData.placementKeys || []).map((placementKey, index) => ({
      placementKey,
      displayOrder: index,
      isActive: true,
    }));

    const mob = advertisementData.mobilePlacement;
    if (mob?.enabled) {
      placements.push({
        mobileScreen: mob.screen,
        mobileSlot: mob.slot,
        mobilePlatform: mob.platform || 'all',
        actionType: mob.actionType !== 'none' ? mob.actionType : null,
        actionTarget: mob.actionTarget || null,
        startAtUtc: mob.startAt ? new Date(mob.startAt).toISOString() : null,
        endAtUtc: mob.endAt ? new Date(mob.endAt).toISOString() : null,
        displayOrder: placements.length,
        isActive: true,
      });
    }

    const payload = {
      titleEn: advertisementData.titleEn || null,
      titleAr: advertisementData.titleAr || null,
      descriptionEn: advertisementData.descriptionEn || null,
      descriptionAr: advertisementData.descriptionAr || null,
      imageUrl,
      link: advertisementData.link || null,
      showCta: advertisementData.showCta !== false,
      ctaTextEn: advertisementData.ctaTextEn || null,
      ctaTextAr: advertisementData.ctaTextAr || null,
      isActive: advertisementData.isActive !== false,
      displayOrder: Number(advertisementData.displayOrder) || 0,
      placements,
    };
    const response = await api.post('/api/advertisements', payload);
    return response.data;
  },

  async update(id, advertisementData) {
    let imageUrl = advertisementData.imageUrl;
    if (advertisementData.image instanceof File) {
      const uploadResult = await imageService.uploadImages([advertisementData.image], 'advertisements');
      if (!uploadResult?.success || !uploadResult?.relativePaths?.length) {
        throw new Error(uploadResult?.message || 'Image upload failed');
      }
      imageUrl = uploadResult.relativePaths[0];
    }
    const placementKeys = advertisementData.placementKeys || (advertisementData.placements || []).filter((p) => !p.mobileScreen).map((p) => (typeof p === 'string' ? p : p.placementKey));
    const placements = placementKeys.map((key, index) =>
      typeof key === 'string'
        ? { placementKey: key, displayOrder: index, isActive: true }
        : { placementKey: key.placementKey, displayOrder: key.displayOrder ?? index, isActive: key.isActive !== false }
    );

    const mob = advertisementData.mobilePlacement;
    if (mob?.enabled) {
      placements.push({
        mobileScreen: mob.screen,
        mobileSlot: mob.slot,
        mobilePlatform: mob.platform || 'all',
        actionType: mob.actionType !== 'none' ? mob.actionType : null,
        actionTarget: mob.actionTarget || null,
        startAtUtc: mob.startAt ? new Date(mob.startAt).toISOString() : null,
        endAtUtc: mob.endAt ? new Date(mob.endAt).toISOString() : null,
        displayOrder: placements.length,
        isActive: true,
      });
    }

    const payload = {
      titleEn: advertisementData.titleEn ?? null,
      titleAr: advertisementData.titleAr ?? null,
      descriptionEn: advertisementData.descriptionEn ?? null,
      descriptionAr: advertisementData.descriptionAr ?? null,
      imageUrl: imageUrl ?? advertisementData.imageUrl,
      link: advertisementData.link ?? null,
      showCta: advertisementData.showCta !== false,
      ctaTextEn: advertisementData.ctaTextEn ?? null,
      ctaTextAr: advertisementData.ctaTextAr ?? null,
      isActive: advertisementData.isActive !== false,
      displayOrder: Number(advertisementData.displayOrder) ?? 0,
      placements,
      deletePlacementIds: advertisementData.deletePlacementIds ?? [],
    };
    const response = await api.put(`/api/advertisements/${id}`, payload);
    return response.data;
  },

  async delete(id) {
    const response = await api.delete(`/api/advertisements/${id}`);
    return response.data;
  },
};

export default advertisementService;
